import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import SystemDiagram from "@/components/SystemDiagram";
import { Eyebrow, H2, Body, Section, CtaLink, StatusChip } from "@/components/Type";
import { useWaitlist } from "@/context/waitlist";
import { PILLARS } from "@/content/pillars";
import { PROGRAMMES } from "@/content/programmes";
import { PRODUCTS } from "@/content/products";
import { ARTICLES } from "@/content/articles";

export default function HomePage() {
  const openWaitlist = useWaitlist();

  return (
    <>
      <Seo
        title="W8ED — Discipline Over Dopamine"
        description="W8ED is a science-led human health company. Eight pillars, one system — compounded through time."
        path="/"
      />

      {/* 1 — HERO */}
      <section className="px-6 pb-28 pt-36 sm:pb-36 sm:pt-48">
        <div className="mx-auto max-w-6xl">
          <Eyebrow>W8ED — Human Health</Eyebrow>
          <h1 className="mt-8 max-w-4xl font-display text-5xl font-extrabold leading-[1.02] tracking-tight text-[#f5f2ea] sm:text-7xl lg:text-8xl">
            THE WEIGHT OF WHAT YOU DO.
          </h1>
          <p className="mt-8 max-w-xl text-lg leading-relaxed text-[#8e8a80]">
            W8ED is building a better way to understand, improve and sustain human health —
            through science, nutrition, performance, products and behaviour.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <CtaLink to="/science" testId="hero-explore-system">EXPLORE THE SYSTEM</CtaLink>
            <button
              type="button"
              data-testid="hero-join-w8list"
              onClick={openWaitlist}
              className="mt-8 font-mono text-[10px] tracking-[0.3em] uppercase text-[#57534a] transition-colors hover:text-[#C9A227]"
            >
              JOIN THE W8LIST →
            </button>
          </div>
        </div>
      </section>

      {/* 2 — FRAGMENTATION */}
      <Section>
        <Eyebrow>The Problem</Eyebrow>
        <H2 className="mt-6">HEALTH ISN'T ONE THING.</H2>
        <Body>
          Nutrition is treated separately from movement. Movement separately from muscle. Sleep
          separately from recovery. Behaviour separately from everything else.
        </Body>
        <Body>But biology does not work in categories.</Body>
        <p className="mt-6 max-w-2xl font-mono text-xs tracking-[0.2em] uppercase leading-relaxed text-[#b8b3a6]">
          W8ED brings the system back together.
        </p>
      </Section>

      {/* 3 — THE SYSTEM */}
      <Section>
        <Eyebrow>The W8ED System</Eyebrow>
        <H2 className="mt-6">THE BODY IS A SYSTEM.</H2>
        <Body>
          Eight fundamental pillars of human health and performance — interconnected, and viewed
          through time. Change one, and the others feel it.
        </Body>
        <div className="mt-16">
          <SystemDiagram />
        </div>
      </Section>

      {/* 4 — TIME */}
      <Section>
        <Eyebrow>Time</Eyebrow>
        <H2 className="mt-6">TIME CHANGES EVERYTHING.</H2>
        <Body>
          A single meal matters. A single workout matters. A single night's sleep matters. But
          repeated signals over months and years matter differently. The body adapts. The system
          changes. The effects accumulate.
        </Body>
        <p className="mt-10 font-mono text-xs sm:text-sm tracking-[0.25em] text-[#b8b3a6]">
          8 PILLARS <span className="text-[#C9A227]">×</span> TIME{" "}
          <span className="text-[#C9A227]">→</span> ADAPTATION{" "}
          <span className="text-[#C9A227]">→</span> HUMAN HEALTH
        </p>
      </Section>

      {/* 5 — PHILOSOPHY */}
      <Section>
        <Eyebrow>The W8ED Philosophy</Eyebrow>
        <H2 className="mt-6">DISCIPLINE &gt; DOPAMINE.</H2>
        <Body>
          Modern environments are optimised for immediate reward. Many meaningful health outcomes
          require delayed gratification.
        </Body>
        <Body>
          The point is not that dopamine is bad. The point is that human behaviour constantly
          balances immediate reward against future outcomes. Discipline is the capacity to
          repeatedly choose what matters later.
        </Body>
      </Section>

      {/* 6 — SCIENCE */}
      <Section>
        <Eyebrow>Science</Eyebrow>
        <H2 className="mt-6">WE DON'T JUST ASK WHAT WORKS. WE ASK WHY.</H2>
        <Body>
          W8ED is evidence-informed by design. The eight pillars are the map; the research is the
          terrain.
        </Body>
        <div className="mt-12 grid grid-cols-2 gap-px border border-[#1f1e1a] bg-[#1f1e1a] sm:grid-cols-4">
          {PILLARS.map((p) => (
            <Link
              key={p.id}
              to={`/science#${p.id}`}
              className="group bg-[#0a0a09] p-6 transition-colors hover:bg-[#101009]"
            >
              <p className="font-mono text-[9px] tracking-[0.3em] text-[#57534a] group-hover:text-[#C9A227] transition-colors">
                {p.number} / 08
              </p>
              <p className="mt-3 font-display text-sm font-bold tracking-[0.15em] text-[#b8b3a6] group-hover:text-[#f5f2ea] transition-colors">
                {p.name}
              </p>
            </Link>
          ))}
        </div>
        <CtaLink to="/science" testId="home-explore-science">EXPLORE SCIENCE</CtaLink>
      </Section>

      {/* 7 — PRODUCTS */}
      <Section>
        <Eyebrow>Products</Eyebrow>
        <H2 className="mt-6">SCIENCE SHOULD BECOME PRACTICAL.</H2>
        <Body>
          Research matters. But what matters just as much is what happens when that knowledge
          becomes something people can actually use every day.
        </Body>
        <p className="mt-8 font-mono text-[10px] tracking-[0.25em] leading-loose text-[#57534a]">
          SCIENCE <span className="text-[#C9A227]">→</span> FORMULATION{" "}
          <span className="text-[#C9A227]">→</span> PRODUCT <span className="text-[#C9A227]">→</span>{" "}
          BEHAVIOUR <span className="text-[#C9A227]">→</span> REPEAT{" "}
          <span className="text-[#C9A227]">→</span> ADAPTATION
        </p>
        <div className="mt-12 grid gap-px border border-[#1f1e1a] bg-[#1f1e1a] sm:grid-cols-3">
          {PRODUCTS.slice(0, 3).map((p) => (
            <Link key={p.id} to={`/products#${p.id}`} className="bg-[#0a0a09] p-8 transition-colors hover:bg-[#101009]">
              <StatusChip status={p.status} />
              <p className="mt-5 font-display text-lg font-bold tracking-wide text-[#f5f2ea]">{p.name}</p>
              <p className="mt-3 text-sm leading-relaxed text-[#8e8a80]">{p.line}</p>
            </Link>
          ))}
        </div>
        <CtaLink to="/products" testId="home-view-products">VIEW PRODUCTS</CtaLink>
      </Section>

      {/* 8 — PROGRAMMES */}
      <Section>
        <Eyebrow>Programmes</Eyebrow>
        <H2 className="mt-6">HEALTH IS SOMETHING WE BUILD.</H2>
        <Body>
          Structure beats motivation. W8ED programmes turn the system into something repeatable —
          measured, reviewed and adapted over time.
        </Body>
        <div className="mt-12 grid gap-px border border-[#1f1e1a] bg-[#1f1e1a] sm:grid-cols-2 lg:grid-cols-4">
          {PROGRAMMES.map((p) => (
            <Link key={p.id} to={`/programmes#${p.id}`} className="bg-[#0a0a09] p-8 transition-colors hover:bg-[#101009]">
              <StatusChip status={p.status} />
              <p className="mt-5 font-display text-base font-bold tracking-wide text-[#f5f2ea]">{p.name}</p>
              <p className="mt-3 text-sm leading-relaxed text-[#8e8a80]">{p.whoFor}</p>
            </Link>
          ))}
        </div>
        <CtaLink to="/programmes" testId="home-explore-programmes">EXPLORE PROGRAMMES</CtaLink>
      </Section>

      {/* 9 — TECHNOLOGY */}
      <Section>
        <div className="flex flex-wrap items-center gap-4">
          <Eyebrow>Technology</Eyebrow>
          <StatusChip status="FUTURE W8ED PLATFORM" />
        </div>
        <H2 className="mt-6">TECHNOLOGY THAT CONNECTS THE SYSTEM.</H2>
        <Body>
          Data becomes insight. Insight becomes intervention. Intervention becomes feedback. And
          feedback, over time, becomes adaptation.
        </Body>
        <div className="mt-12 max-w-md">
          {["DATA", "INSIGHT", "INTERVENTION", "FEEDBACK", "ADAPTATION"].map((s, i) => (
            <div key={s}>
              <p className="border border-[#1f1e1a] px-6 py-4 font-mono text-[11px] tracking-[0.3em] text-[#b8b3a6]">
                {s}
              </p>
              {i < 4 && <p className="py-1 text-center font-mono text-[#C9A227]">↓</p>}
            </div>
          ))}
        </div>
        <CtaLink to="/technology" testId="home-explore-technology">EXPLORE TECHNOLOGY</CtaLink>
      </Section>

      {/* 10 — THINKING */}
      <Section>
        <Eyebrow>W8ED Thinking</Eyebrow>
        <H2 className="mt-6">IDEAS, WRITTEN DOWN.</H2>
        <div className="mt-12 grid gap-px border border-[#1f1e1a] bg-[#1f1e1a] sm:grid-cols-2 lg:grid-cols-4">
          {ARTICLES.slice(0, 4).map((a) => (
            <Link key={a.slug} to={`/thinking/${a.slug}`} className="bg-[#0a0a09] p-8 transition-colors hover:bg-[#101009]">
              <p className="font-mono text-[9px] tracking-[0.3em] text-[#C9A227]">{a.topic.toUpperCase()}</p>
              <p className="mt-4 font-display text-lg font-bold leading-snug text-[#f5f2ea]">{a.title}</p>
              <p className="mt-4 font-mono text-[9px] tracking-[0.25em] text-[#57534a]">{a.status}</p>
            </Link>
          ))}
        </div>
        <CtaLink to="/thinking" testId="home-read-thinking">READ THINKING</CtaLink>
      </Section>

      {/* 11 — FOUNDER */}
      <Section>
        <Eyebrow>Founder</Eyebrow>
        <H2 className="mt-6">WHY I BUILT W8ED.</H2>
        <Body>
          "Not with a product. Not with an app. With a question: can we build a better system for
          human health?"
        </Body>
        <p className="mt-8 font-mono text-[10px] tracking-[0.3em] uppercase text-[#b8b3a6]">
          VIKRAM CHAWLA
        </p>
        <p className="mt-1 font-mono text-[9px] tracking-[0.3em] uppercase text-[#57534a]">
          FOUNDER, W8ED
        </p>
        <CtaLink to="/founder" testId="home-meet-founder">MEET THE FOUNDER</CtaLink>
      </Section>

      {/* 12 — ABOUT */}
      <Section>
        <Eyebrow>About</Eyebrow>
        <H2 className="mt-6">
          WEIGHT IS NOT JUST WHAT YOU CARRY.
          <br />
          <span className="text-[#C9A227]">IT IS WHAT ACCUMULATES.</span>
        </H2>
        <CtaLink to="/about" testId="home-about">ABOUT W8ED</CtaLink>
      </Section>

      {/* 13 — CONTACT */}
      <Section>
        <Eyebrow>Contact</Eyebrow>
        <H2 className="mt-6">LET'S TALK.</H2>
        <Body>
          Building, researching, partnering, working — or simply curious. We'd like to hear from
          you.
        </Body>
        <CtaLink to="/contact" testId="home-contact">CONTACT W8ED</CtaLink>
      </Section>

      {/* 14 — FINAL CTA */}
      <section className="border-t border-[#1f1e1a] px-6 py-32 text-center sm:py-44">
        <h2 className="font-display text-4xl font-extrabold tracking-tight text-[#f5f2ea] sm:text-6xl">
          YOU W8ED LONG ENOUGH.
        </h2>
        <p className="mt-6 font-mono text-[11px] tracking-[0.35em] uppercase text-[#C9A227]">
          Welcome to W8ED.
        </p>
        <button
          type="button"
          data-testid="home-final-join"
          onClick={openWaitlist}
          className="mt-10 border border-[#3a3524] px-8 py-4 font-mono text-[11px] tracking-[0.3em] uppercase text-[#C9A227] transition-colors hover:border-[#C9A227]"
        >
          JOIN THE W8LIST
        </button>
      </section>
    </>
  );
}
